import { useState } from "react";
import { Icon } from "../components/Icon";
import { addExposure, characterCounts, storyWordCounts } from "../lib/exposure.mjs";
import { shuffled } from "../lib/engine.mjs";
import { useLanguage, useStore } from "../app/useStore";
import { formatTime } from "../format";
import type { Story } from "../types";

export function StoriesPage({ itemId }: { itemId?: string }) {
  const ctx = useLanguage();
  const story = ctx.stories.find((s) => s.id === itemId);
  return story ? <StoryView story={story} /> : <StoryLibrary />;
}

function useStoryStats() {
  const { progress: p } = useLanguage();
  return (s: Story) => (p.exposure.stories[s.id] ||= { plays: 0, reads: 0, results: [], position: 0 });
}

function StoryLibrary() {
  const ctx = useLanguage();
  const stats = useStoryStats();
  const { stories, manifest, definition: d } = ctx;
  const duration = (s: Story) => s.sentences.reduce((sum, line) => sum + (manifest[line.audio]?.duration || 0), 0);
  const listened = stories.filter((s) => stats(s).plays > 0).length;
  return (
    <>
      <div className="view-heading">
        <div>
          <p className="overline">Language in context</p>
          <h1>A story worth another listen.</h1>
          <p>Small moments, familiar words. Listen first, then explore what you understood.</p>
        </div>
        <span className="count-badge">{listened} of {stories.length} listened</span>
      </div>
      <div className="story-grid">
        {stories.map((s, i) => {
          const st = stats(s);
          const last = st.results.at(-1);
          return (
            <a key={s.id} className={"story-card story-color-" + (i % 4)} href={ctx.href("stories", s.id)}>
              <div className="story-art">
                <span className="story-glyph" lang={d.id}>{s.title.slice(0, 2)}</span>
                <span className="story-number">{String(i + 1).padStart(2, "0")}</span>
                <span className="art-play"><Icon name="play" /></span>
              </div>
              <div className="story-card-body">
                <p className="story-theme">{s.theme}</p>
                <h2 lang={d.id}>{s.title}</h2>
                <p>{s.titleEn}</p>
                <div className="story-meta">
                  <span><Icon name="audio" />{formatTime(duration(s))}</span>
                  <span>{s.wordIds.length} words</span>
                  {st.plays > 0 && <span className="positive"><Icon name="check" />Listened</span>}
                </div>
                {last && <p className="small-note">Last check: {last.correct}/{last.total}{last.assisted ? " · English help" : ""}</p>}
              </div>
            </a>
          );
        })}
      </div>
      <p className="footnote">{stories.length} original stories using your {ctx.deck.length} deck entries and supporting vocabulary.</p>
    </>
  );
}

function StoryView({ story: s }: { story: Story }) {
  const ctx = useLanguage();
  const store = useStore();
  const stats = useStoryStats();
  const { progress: p, definition: d, player, manifest } = ctx;
  const st = stats(s);
  const program = ctx.programs.find((x) => x.id === "story-" + s.id);
  const [readMarked, setReadMarked] = useState(false);
  const duration = s.sentences.reduce((sum, line) => sum + (manifest[line.audio]?.duration || 0), 0);

  const ps = store.playerState;
  const active = !!program && ps.program?.id === program.id && ps.languageId === d.id;
  const playing = active && ps.playing;
  const cues = program?.cues.filter((c) => c.kind === "audio") ?? [];
  const current = active ? cues.findIndex((c) => ps.position >= c.start && ps.position < c.end) : -1;

  const toggleTranscript = () => { p.storyPrefs.transcript = !p.storyPrefs.transcript; ctx.save(); };
  const markRead = () => {
    if (readMarked) { setReadMarked(false); return; }
    addExposure(p.exposure, {
      source: "story", kind: "reading", wordCounts: storyWordCounts(s),
      characterCounts: d.capabilities.characters ? characterCounts(s.sentences.map((l) => l.text).join("")) : {},
    });
    st.reads++; setReadMarked(true); ctx.activity("stories", s.id, s.titleEn);
  };

  return (
    <>
      <a className="back-link" href={ctx.href("stories")}><Icon name="back" /> All stories</a>
      <div className="view-heading"><div><p className="overline">{s.theme}</p><h1 lang={d.id}>{s.title}</h1><p>{s.titleEn}</p></div></div>
      <section className="story-listen-panel">
        <div className="story-listen-art" lang={d.id}>{s.title.slice(0, 2)}</div>
        <div className="story-listen-copy">
          <span className="overline">{formatTime(duration)} listening</span>
          <h2>Let the story come to you.</h2>
          <p>Listen once for the feeling. Listen again for the details.</p>
          <div className="button-row">
            <button className="button" id="story-play" disabled={!program} onClick={() => {
              if (playing) player.pause(); else ctx.playProgram(program);
              ctx.activity("stories", s.id, s.titleEn);
            }}>
              <Icon name={playing ? "pause" : "play"} />{playing ? " Pause story" : active && ps.snapshot?.completed ? " Listen again" : active && ps.position > 0 ? " Continue story" : " Listen to story"}
            </button>
            <button className="text-link" id="restart-story" disabled={!program} onClick={() => ctx.playProgram(program, { restart: true })}><Icon name="repeat" /> Restart</button>
          </div>
          <p className="small-note" id="story-listen-status">
            {active && ps.error ? "Audio couldn’t play. Tap Listen to retry." : `${st.plays} complete ${st.plays === 1 ? "listen" : "listens"}`}
          </p>
        </div>
      </section>
      <div className="story-tools">
        <button className="text-link" id="study-story-words" onClick={() => ctx.studyWords(s.wordIds, s.titleEn)}><Icon name="book" /> Study these {s.wordIds.length} words</button>
        <span>{st.reads} confirmed reads</span>
      </div>
      <section className="transcript-section">
        <button className="transcript-heading" id="transcript-toggle" aria-expanded={p.storyPrefs.transcript} onClick={toggleTranscript}>
          <span><Icon name="stories" /> {d.name} transcript</span><span>{p.storyPrefs.transcript ? "Hide" : "Show"}</span>
        </button>
        <div id="transcript-body" hidden={!p.storyPrefs.transcript}>
          <p className="hint">Tap an underlined word to explore it.</p>
          <div className="transcript-text" lang={d.id}>
            {s.sentences.map((line, i) => (
              <p key={line.id} className={active && playing && i === current ? "current-sentence" : undefined}>
                {line.tokens.map((t, j) => t.wordId
                  ? <button key={j} className="transcript-word" onClick={() => ctx.openWord(t.wordId as string)}>{t.text}</button>
                  : t.text)}
              </p>
            ))}
          </div>
          <button className="button secondary" id="mark-story-read" onClick={markRead}>{readMarked ? "Read again" : "Mark as read"}</button>
          <p className="small-note" id="read-note">{readMarked ? "Reading exposure saved." : "Finished reading? Save one reading pass to your exposure stats."}</p>
        </div>
      </section>
      <StoryQuiz story={s} />
    </>
  );
}

interface Quiz { index: number; results: { selected: number; correct: boolean }[]; assisted: boolean; choices: number[]; done: boolean }

function StoryQuiz({ story: s }: { story: Story }) {
  const ctx = useLanguage();
  const stats = useStoryStats();
  const { progress: p, definition: d } = ctx;
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const text = (x: { target?: string; ja?: string }) => x.target || x.ja || "";

  if (!quiz) {
    return (
      <section className="story-quiz" id="story-quiz">
        <div className="quiz-intro">
          <div><p className="overline">Check understanding</p><h2>What stayed with you?</h2><p>{s.questions.length} questions in {d.name}. English help is optional.</p></div>
          <button className="button secondary" id="begin-quiz" onClick={() => setQuiz({ index: 0, results: [], assisted: p.storyPrefs.english, choices: shuffled([0, 1, 2, 3]), done: false })}>Start the check</button>
        </div>
      </section>
    );
  }

  if (quiz.done) {
    const correct = quiz.results.filter((r) => r.correct).length;
    return (
      <section className="story-quiz" id="story-quiz">
        <div className="quiz-summary">
          <p className="overline">Comprehension check complete</p>
          <h2>{correct} <span>/ {s.questions.length}</span></h2>
          <p>{quiz.assisted ? "With English help" : "Answered without English subtitles"}</p>
          <div className="result-list">
            {quiz.results.map((r, i) => (
              <div key={i}><span className={r.correct ? "positive" : "negative"}><Icon name={r.correct ? "check" : "repeat"} /></span><p lang={d.id}>{text(s.questions[i]!)}</p></div>
            ))}
          </div>
          <div className="button-row">
            <button className="button" onClick={() => ctx.studyWords(s.wordIds, s.titleEn)}>Study story words</button>
            <button className="button secondary" onClick={() => setQuiz(null)}>Try again</button>
          </div>
        </div>
      </section>
    );
  }

  const q = s.questions[quiz.index]!;
  const result = quiz.results[quiz.index];
  const answered = !!result;
  const last = quiz.index === s.questions.length - 1;

  const answer = (n: number) => setQuiz({ ...quiz, results: [...quiz.results, { selected: n, correct: n === q.correct }] });
  const next = () => {
    if (last) {
      const st = stats(s);
      st.results.push({ correct: quiz.results.filter((r) => r.correct).length, total: s.questions.length, assisted: quiz.assisted, at: Date.now() });
      st.results = st.results.slice(-30);
      ctx.activity("stories", s.id, s.titleEn);
      setQuiz({ ...quiz, done: true });
    } else setQuiz({ ...quiz, index: quiz.index + 1, choices: shuffled([0, 1, 2, 3]) });
  };

  return (
    <section className="story-quiz" id="story-quiz">
      <div className="quiz-question-heading">
        <span>Question {quiz.index + 1} of {s.questions.length}</span>
        <label className="toggle-label">
          <input type="checkbox" id="question-english" checked={p.storyPrefs.english} onChange={(e) => {
            p.storyPrefs.english = e.target.checked; ctx.save();
            if (e.target.checked) setQuiz({ ...quiz, assisted: true });
          }} /> English help
        </label>
      </div>
      <h2 className="story-question" lang={d.id}>{text(q)}</h2>
      {p.storyPrefs.english && <p className="question-subtitle">{q.en}</p>}
      <div className="answer-grid">
        {quiz.choices.map((n) => (
          <button key={n} disabled={answered}
            className={"answer-option " + (answered ? (n === q.correct ? "correct" : n === result.selected ? "incorrect" : "") : "")}
            onClick={() => answer(n)}>
            <span lang={d.id}>{text(q.answers[n]!)}</span>
            {p.storyPrefs.english && <small>{q.answers[n]!.en}</small>}
          </button>
        ))}
      </div>
      {answered ? (
        <div className="round-feedback">
          <div><strong>{result.correct ? "You got it." : "Give this one another look."}</strong><p lang={d.id}>{text(q.answers[q.correct]!)}</p></div>
          <button className="button" ref={(el) => el?.focus({ preventScroll: true })} id="next-story-question" onClick={next}>{last ? "See results" : "Next question"}</button>
        </div>
      ) : <p className="hint">Take your time. There’s no clock here.</p>}
    </section>
  );
}
